import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useNavigate, Link } from "react-router-dom";

function ProductList() {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);          // ✅ NEW
  const [categories, setCategories] = useState([]);  // ✅ NEW

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  // ✅ pagination
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 10;

  // ✅ filters
  const [search, setSearch] = useState("");       // name/id
  const [brandFilter, setBrandFilter] = useState(""); // brand_id
  const [catFilter, setCatFilter] = useState("");     // cat_id
  const [applyFilter, setApplyFilter] = useState(0);

  const baseURL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const token = localStorage.getItem("profileToken");

  // ✅ fetch products
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line
  }, []);

  const fetchProducts = async () => {
    try {
      if (!token) {
        Swal.fire("Unauthorized", "Please login first!", "warning");
        setLoading(false);
        return;
      }
      const res = await axios.get(`${baseURL}/product/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(res.data.result || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setMessage("Error fetching products");
      setLoading(false);
    }
  };

  // ✅ fetch brands for dropdown (same as AddProduct)
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await axios.get(`${baseURL}/brand/list`, {
          headers: { "x-access-token": token || "" },
        });
        if (res.data?.status) setBrands(res.data.result || []);
      } catch (err) {
        console.error("❌ Error fetching brands:", err);
      }
    };
    fetchBrands();
  }, [baseURL, token]);

  // ✅ fetch categories for selected brand (same as AddProduct)
  useEffect(() => {
    if (!brandFilter) {
      setCategories([]);
      setCatFilter("");
      return;
    }

    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${baseURL}/category/brand/${brandFilter}`, {
          headers: { "x-access-token": token || "" },
        });
        if (res.data?.status) setCategories(res.data.result || []);
        else setCategories([]);
      } catch (err) {
        setCategories([]);
        console.error("❌ Error fetching categories:", err);
      }
    };

    fetchCategories();
  }, [brandFilter, baseURL, token]);

  // ✅ counts (brand status based)
  const activeCount = useMemo(
    () => products.filter((p) => Number(p.brand_status) === 1).length,
    [products]
  );
  const inactiveCount = useMemo(
    () => products.filter((p) => Number(p.brand_status) === 0).length,
    [products]
  );

  // ✅ base visible (active/inactive brand products)
  const baseVisibleProducts = useMemo(() => {
    return products.filter((p) =>
      showInactive ? Number(p.brand_status) === 0 : Number(p.brand_status) === 1
    );
  }, [products, showInactive]);

  // ✅ apply filters ONLY when Search clicked
  const filteredProducts = useMemo(() => {
    // eslint-disable-next-line
    const _ = applyFilter;

    let list = [...baseVisibleProducts];
    const q = search.trim().toLowerCase();

    if (q) {
      list = list.filter((p) => {
        const nameMatch = (p.pro_name || "").toLowerCase().includes(q);
        const idMatch = String(p.id) === q;
        return nameMatch || idMatch;
      });
    }

    if (brandFilter) {
      list = list.filter((p) => String(p.brand_id) === String(brandFilter));
    }

    if (catFilter) {
      list = list.filter((p) => String(p.cat_id) === String(catFilter));
    }

    return list;
  }, [baseVisibleProducts, applyFilter, search, brandFilter, catFilter]);

  // ✅ pagination
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage) || 1;
  const indexOfLast = currentPage * productsPerPage;
  const indexOfFirst = indexOfLast - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirst, indexOfLast);

  const paginate = (pageNum) => setCurrentPage(pageNum);

  // ✅ whenever toggle changes, go page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [showInactive]);

  const onSearchClick = () => {
    setCurrentPage(1);
    setApplyFilter((p) => p + 1);
  };

  const onResetClick = () => {
    setSearch("");
    setBrandFilter("");
    setCatFilter("");
    setCategories([]);
    setCurrentPage(1);
    setApplyFilter((p) => p + 1);
  };

  const deleteProduct = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This product will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${baseURL}/product/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      Swal.fire("Deleted!", "Product has been deleted.", "success");
      fetchProducts();
    } catch (err) {
      console.error(err);
      Swal.fire("Error!", "Failed to delete product.", "error");
    }
  };

  if (loading) return <p className="text-center mt-5">Loading products...</p>;

  return (
    <div className="container my-5 p-4 border rounded shadow bg-white">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 text-success">
          {showInactive ? "Inactive Brand Products" : "Product List"}
          <span className="badge bg-secondary ms-2">
            {showInactive ? inactiveCount : activeCount}
          </span>
        </h3>

        <div className="d-flex gap-2">
          <button
            className={`btn ${
              showInactive ? "btn-outline-success" : "btn-outline-danger"
            } rounded-pill px-4`}
            onClick={() => setShowInactive((p) => !p)}
          >
            {showInactive
              ? `Active Brand Products (${activeCount})`
              : `Inactive Brand Products (${inactiveCount})`}
          </button>

          <Link className="btn btn-success rounded-pill px-4" to="/add-product">
            <i className="fas fa-plus me-2"></i> Add Product
          </Link>
        </div>
      </div>

      {message && <div className="alert alert-info text-center">{message}</div>}

      {/* ✅ Filters Row (dynamic like AddProduct) */}
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search by product name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="col-md-3">
          <select
            className="form-select"
            value={brandFilter}
            onChange={(e) => {
              setBrandFilter(e.target.value);
              setCatFilter("");     // ✅ reset category
              setCategories([]);    // ✅ clear old categories until fetch
            }}
          >
            <option value="">Select Brand (All)</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3">
          <select
            className="form-select"
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            disabled={!brandFilter}
          >
            <option value="">Select Category (All)</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.cat_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-2 d-flex gap-2">
          <button className="btn btn-success w-100" onClick={onSearchClick}>
            Search
          </button>
          <button className="btn btn-outline-secondary w-100" onClick={onResetClick}>
            Reset
          </button>
        </div>
      </div>

      <table className="table table-striped table-bordered align-middle text-center">
        <thead className="table-success">
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Image</th>
            <th>Weight</th>
            <th>Brand</th>
            <th>Category</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {currentProducts.length === 0 ? (
            <tr>
              <td colSpan="7" className="py-4 text-muted">
                No products found.
              </td>
            </tr>
          ) : (
            currentProducts.map((product, index) => (
              <tr
                key={product.id}
                className={Number(product.brand_status) === 0 ? "table-secondary" : ""}
                style={Number(product.brand_status) === 0 ? { opacity: 0.9 } : {}}
              >
                <td>{indexOfFirst + index + 1}</td>
                <td>{product.pro_name || "-"}</td>

                <td>
                  {product.pro_img ? (
                    <img
                      src={`${baseURL}/uploads/${product.pro_img}`}
                      alt="pro_img"
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </td>

                <td>
                  {product.pro_weight || "-"} {product.w_unit || "-"}
                </td>

                <td>
                  {product.brand_name || "-"}
                  {Number(product.brand_status) === 0 && (
                    <span className="badge bg-danger ms-2">Inactive Brand</span>
                  )}
                </td>

                <td>{product.category_name || "-"}</td>

                <td>
                  <button
                    className="btn btn-sm btn-primary me-2"
                    title="Edit"
                    onClick={() => navigate(`/update-product/${product.id}`)}
                  >
                    <i className="fa fa-edit"></i>
                  </button>

                  <button
                    className="btn btn-sm btn-danger"
                    title="Delete"
                    onClick={() => deleteProduct(product.id)}
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* ✅ Pagination */}
      {filteredProducts.length > 0 && totalPages > 1 && (
        <nav>
          <ul className="pagination">
            {Array.from({ length: totalPages }, (_, i) => (
              <li
                key={i + 1}
                className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
              >
                <button className="page-link" onClick={() => paginate(i + 1)}>
                  {i + 1}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}

export default ProductList;
